<template>
  <div class="modal-overlay" @click="closeModal" v-if="isOpen">
    <div class="modal" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">Nuova Analisi</h2>
        <button class="close-button" @click="closeModal" aria-label="Chiudi">
          ×
        </button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="handleSubmit" class="analysis-form">
          <!-- Company Name -->
          <div class="form-group">
            <label for="company_name" class="form-label">
              Nome Azienda <span class="required">*</span>
            </label>
            <input
              type="text"
              id="company_name"
              v-model="formData.company_name"
              @input="fetchCompanySuggestions"
              @focus="showSuggestions = true"
              @blur="hideSuggestions"
              class="form-input"
              placeholder="Es: Nike"
              required
            />
            <ul v-if="showSuggestions && suggestions.length > 0" class="suggestions-list">
              <li
                v-for="suggestion in suggestions"
                :key="suggestion.company_id"
                @mousedown="selectSuggestion(suggestion)"
                class="suggestion-item"
              >
                {{ suggestion.company_name }}
              </li>
            </ul>
          </div>

          <!-- Sector -->
          <div class="form-group">
            <label for="sector" class="form-label">Settore</label>
            <input
              type="text"
              id="sector"
              v-model="formData.sector"
              class="form-input"
              placeholder="Es: Food & Beverage"
            />
          </div>

          <!-- Website -->
          <div class="form-group">
            <label for="website" class="form-label">Sito Web <span class="required">*</span></label>
            <input
              type="url"
              id="website"
              v-model="formData.website"
              class="form-input"
              placeholder="https://example.com"
            />
          </div>

          <!-- Instagram URL -->
          <div class="form-group">
            <label for="instagram_url" class="form-label">Instagram <span class="required">*</span></label>
            <input
              type="url"
              id="instagram_url"
              v-model="formData.instagram_url"
              class="form-input"
              placeholder="https://instagram.com/username"
            />
          </div>

          <!-- Facebook URL -->
          <div class="form-group">
            <label for="facebook_url" class="form-label">Facebook</label>
            <input
              type="url"
              id="facebook_url"
              v-model="formData.facebook_url"
              class="form-input"
              placeholder="https://facebook.com/page"
            />
          </div>

          <!-- LinkedIn URL -->
          <div class="form-group">
            <label for="linkedin_url" class="form-label">LinkedIn</label>
            <input
              type="url"
              id="linkedin_url"
              v-model="formData.linkedin_url"
              class="form-input"
              placeholder="https://linkedin.com/company/name"
            />
          </div>

          <div class="form-footer">
            <button type="button" @click="closeModal" class="button button-secondary">
              Annulla
            </button>
            <button type="submit" class="button button-primary" :disabled="loading">
              {{ loading ? 'Creazione...' : 'Crea Analisi' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabaseClient';
import { useAuthStore } from '@/stores/authStore';

export default {
  name: 'CreateAnalysisModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formData: {
        company_name: '',
        sector: '',
        website: '',
        instagram_url: '',
        facebook_url: '',
        linkedin_url: '',
      },
      suggestions: [],
      showSuggestions: false,
      loading: false,
    };
  },
  methods: {
    async fetchCompanySuggestions() {
      if (this.formData.company_name.length > 1) {
        try {
          const { data, error } = await supabase
            .from('companies')
            .select('company_id, company_name, sector, website')
            .ilike('company_name', `%${this.formData.company_name}%`)
            .limit(5);

          if (error) {
            console.error('Error fetching company suggestions:', error.message);
            return;
          }

          this.suggestions = data || [];
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        }
      } else {
        this.suggestions = [];
      }
    },

    selectSuggestion(suggestion) {
      this.formData.company_name = suggestion.company_name;
      this.formData.sector = suggestion.sector || '';
      this.formData.website = suggestion.website || '';
      this.showSuggestions = false;
      this.suggestions = [];
    },

    hideSuggestions() {
      // Delay to allow click event on suggestion
      setTimeout(() => {
        this.showSuggestions = false;
      }, 200);
    },

    async handleSubmit() {
      // Validate required fields
    if (!this.formData.company_name || !this.formData.website || !this.formData.instagram_url) {
        alert("Tutti i campi obbligatori devono essere compilati.");
        return;
      }


      // Get user ID from auth store
      const authStore = useAuthStore();
      const user = authStore.user;
      if (!user) {
        alert('Devi essere autenticato per creare un\'analisi.');
        return;
      }

      this.loading = true;

      try {
        const analysisData = {
          user_id: user.id,
          company_name: this.formData.company_name,
          sector: this.formData.sector || undefined,
          website: this.formData.website || undefined,
          instagram_url: this.formData.instagram_url || undefined,
          facebook_url: this.formData.facebook_url || undefined,
          linkedin_url: this.formData.linkedin_url || undefined,
        };

        // Remove undefined values
        Object.keys(analysisData).forEach(
          (key) => analysisData[key] === undefined && delete analysisData[key]
        );

        this.$emit('create', analysisData);
        this.resetForm();
        this.closeModal();
      } catch (error) {
        console.error('Error in form submission:', error);
        alert('Si è verificato un errore. Riprova.');
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.formData = {
        company_name: '',
        sector: '',
        website: '',
        instagram_url: '',
        facebook_url: '',
        linkedin_url: '',
      };
      this.suggestions = [];
      this.showSuggestions = false;
    },

    closeModal() {
      this.resetForm();
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 2px solid #2e4eed;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  color: #1a1a1a;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.close-button:hover {
  opacity: 0.7;
}

.modal-body {
  padding: 1.5rem;
}

.analysis-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
}

.form-label {
  font-size: 1rem;
  font-weight: 500;
  color: #1a1a1a;
}

.required {
  color: #ef4444;
}

.form-input {
  width: 100%;
  height: 3rem;
  padding: 0 1rem;
  border: 2px solid #2e4eed;
  background: white;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #56bcee;
  box-shadow: 0 0 0 2px rgba(86, 188, 238, 0.2);
}

.suggestions-list {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #2e4eed;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  list-style: none;
  padding: 0;
  margin: 0;
  box-shadow: 0 4px 12px rgba(46, 78, 237, 0.1);
}

.suggestion-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.suggestion-item:hover {
  background-color: rgba(46, 78, 237, 0.05);
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.button {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  border: 2px solid;
  cursor: pointer;
  transition: all 0.3s ease;
}

.button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-primary {
  background: #2e4eed;
  border-color: #2e4eed;
  color: white;
}

.button-primary:hover:not(:disabled) {
  background: #1e3ed8;
  border-color: #1e3ed8;
}

.button-secondary {
  background: white;
  border-color: #2e4eed;
  color: #2e4eed;
}

.button-secondary:hover {
  background: rgba(46, 78, 237, 0.05);
}

@media (max-width: 768px) {
  .modal {
    max-width: 100%;
    max-height: 100vh;
    height: 100%;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .form-footer {
    flex-direction: column;
  }

  .button {
    width: 100%;
  }
}
</style>
